class BhEmailPopup extends HTMLElement {
  static STORAGE_KEY = 'bh_email_popup';
  static DISMISS_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;

  connectedCallback() {
    this.overlay = this.querySelector('[data-popup-overlay]');
    this.shown = false;
    this.timerId = null;

    if (!this.overlay) return;

    this.querySelector('[data-popup-close]')?.addEventListener('click', () => this.dismiss());
    this.querySelector('[data-popup-success-close]')?.addEventListener('click', () => this.close());

    this.overlay.addEventListener('click', (event) => {
      if (event.target === this.overlay) this.dismiss();
    });

    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.overlay.classList.contains('is-visible')) this.dismiss();
    };
    document.addEventListener('keydown', this.onKeydown);

    // An explicit open request (e.g. the announcement bar) overrides the
    // dismissed/subscribed suppression, so it's wired up unconditionally.
    this.onOpenRequest = () => this.show();
    document.addEventListener('bh:open-signup-popup', this.onOpenRequest);

    // The signup form submits natively and Shopify redirects back to this
    // page; <bh-signup-form> restores its success state on load. Open the
    // popup straight to that state so the visitor actually sees the code.
    this.addEventListener('bh:signup:success', () => this.show());
    const successState = this.querySelector('[data-success-state]');
    if (successState && !successState.hidden) {
      this.show();
      return;
    }

    if (!this.shouldShow()) return;

    if (this.dataset.exitIntent === 'true') {
      this.onMouseOut = (event) => {
        if (event.clientY < 0) {
          document.documentElement.removeEventListener('mouseout', this.onMouseOut);
          this.show();
        }
      };
      document.documentElement.addEventListener('mouseout', this.onMouseOut);
    }

    const delay = (Number(this.dataset.timerDelay) || 5) * 1000;
    this.timerId = setTimeout(() => this.show(), delay);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('bh:open-signup-popup', this.onOpenRequest);
    if (this.timerId) clearTimeout(this.timerId);
  }

  getStorageData() {
    try {
      return JSON.parse(localStorage.getItem(BhEmailPopup.STORAGE_KEY));
    } catch {
      return null;
    }
  }

  setStorageData(data) {
    try {
      localStorage.setItem(BhEmailPopup.STORAGE_KEY, JSON.stringify(data));
    } catch {
      /* localStorage unavailable */
    }
  }

  shouldShow() {
    const data = this.getStorageData();
    if (!data) return true;
    if (data.subscribed) return false;
    if (data.dismissed && data.timestamp) {
      return Date.now() - data.timestamp >= BhEmailPopup.DISMISS_EXPIRY_MS;
    }
    return true;
  }

  show() {
    if (this.shown) return;
    this.shown = true;
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }

    this.overlay.style.display = 'flex';
    this.overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.overlay.classList.add('is-visible'));
    });
  }

  close() {
    this.overlay.classList.remove('is-visible');
    this.overlay.setAttribute('aria-hidden', 'true');

    setTimeout(() => {
      this.overlay.style.display = 'none';
      document.body.style.overflow = '';
    }, 450);
  }

  dismiss() {
    this.setStorageData({ dismissed: true, timestamp: Date.now() });
    this.close();
  }
}

customElements.define('bh-email-popup', BhEmailPopup);
